<template>
    <b-container fluid class="project-group-management">
        <admin-page-header title="평가 그룹 관리"
                           description="프로젝트에 연결하는 평가 그룹(project group)을 생성/수정/삭제합니다. 그룹 코드는 프론트 빌드와 약속된 값이라 생성 후에는 바꿀 수 없습니다."/>

        <b-row>
            <b-col cols="12">
                <b-card no-body class="shadow-sm" header-bg-variant="white">
                    <template #header>
                        <div class="d-flex align-items-center justify-content-between flex-wrap">
                            <div class="d-flex align-items-center">
                                <h5 class="mb-0">평가 그룹 목록</h5>
                                <b-badge v-if="!loading" pill variant="primary" class="ml-2">
                                    {{ groups.length }}건
                                </b-badge>
                            </div>
                            <b-btn size="sm" variant="primary" :disabled="loading" @click="openCreate">
                                <fa icon="plus" class="mr-2" />평가 그룹 추가
                            </b-btn>
                        </div>
                    </template>

                    <b-table
                        class="mb-0 sticky-header-sort-fix"
                        :items="groups"
                        :fields="fields"
                        :busy="loading"
                        show-empty
                        hover
                        responsive="sm"
                        sticky-header="600px"
                        no-border-collapse
                    >
                        <template #empty>
                            <PageEmptyComponent compact text="등록된 평가 그룹이 없습니다." :error="listError" @retry="fetchGroups"/>
                        </template>
                        <template #table-busy>
                            <div class="d-flex align-items-center justify-content-center my-4">
                                <b-spinner class="mr-2" variant="primary" />
                                <span>불러오는 중...</span>
                            </div>
                        </template>

                        <template #cell(group_code)="{ value }">
                            <code>{{ value }}</code>
                        </template>

                        <template #cell(report_type)="{ item }">
                            <b-badge v-if="item.report_type === 'EV_COUNT'" variant="info">
                                EV_COUNT (v3)
                            </b-badge>
                            <span v-else class="text-secondary">기본 계산식 (v2)</span>
                        </template>

                        <template #cell(is_active)="{ value }">
                            <b-badge pill :variant="value ? 'success' : 'secondary'">
                                {{ value ? '활성' : '비활성' }}
                            </b-badge>
                        </template>

                        <template #cell(memo)="{ value }">
                            <span v-if="value" style="word-break: break-all;">{{ value }}</span>
                            <span v-else class="text-muted">-</span>
                        </template>

                        <template #cell(actions)="{ item }">
                            <b-btn size="sm" variant="outline-secondary" class="mr-1" @click="openEdit(item)">
                                수정
                            </b-btn>
                            <b-btn size="sm" variant="outline-danger" :disabled="deletingId === item.id" @click="confirmDelete(item)">
                                <b-spinner v-if="deletingId === item.id" small />
                                <span v-else>삭제</span>
                            </b-btn>
                        </template>
                    </b-table>
                </b-card>
            </b-col>
        </b-row>

        <!-- 생성/수정 공용 모달 -->
        <ProjectGroupFormModal
            :modal-id="formModalId"
            :mode="formMode"
            :group="editingGroup"
            @saved="onSaved"
        />
    </b-container>
</template>

<script>
import { getErrorMessage } from '@core/utils/api/errorMessage';
import PageEmptyComponent from '@shared/components/admin/components/common/PageEmptyComponent.vue';
import ProjectGroupFormModal from './ProjectGroupFormModal.vue';

export default {
    name: 'ProjectGroupManagement',
    components: { ProjectGroupFormModal, PageEmptyComponent },
    data() {
        return {
            groups: [],
            loading: false,
            listError: '',
            deletingId: null,
            formModalId: 'project-group-form-modal',
            formMode: 'create',
            editingGroup: {},
            fields: [
                { key: 'group_code', label: '그룹 코드', thStyle: 'width: 22%;' },
                { key: 'group_name', label: '그룹명', thStyle: 'width: 24%;' },
                { key: 'report_type', label: '보고서 엔진', thStyle: 'width: 14%;' },
                { key: 'is_active', label: '활성', thStyle: 'width: 8%;' },
                { key: 'memo', label: '메모' },
                { key: 'actions', label: '관리', thClass: 'text-center', tdClass: 'text-nowrap', thStyle: 'width: 12%;' },
            ],
        };
    },
    created() {
        this.fetchGroups();
    },
    methods: {
        async fetchGroups() {
            this.loading = true;
            this.listError = '';
            try {
                // 관리 화면은 비활성 그룹도 봐야 하므로 include_inactive=true 로 전부 조회한다.
                const { data } = await this.$axios.get('/project_code/groups', {
                    params: { include_inactive: true },
                });
                this.groups = Array.isArray(data) ? data : [];
            } catch (e) {
                // 실패를 '등록된 평가 그룹이 없습니다'로 보이지 않게 이전 결과를 비우고 표 안에 오류를 띄운다
                this.groups = [];
                if (e?.response?.status === 401) return;
                this.listError = `평가 그룹 목록을 불러오지 못했습니다. ${this.extractError(e)}`.trim();
            } finally {
                this.loading = false;
            }
        },
        // 모달의 show 이벤트(onShow 가 props 로 폼을 채운다)는 동기로 불려, props 가 반영된 다음 tick 에 연다.
        // 바로 열면 첫 '수정' 은 빈 폼, 이후에는 직전에 연 그룹 값으로 열렸다
        openCreate() {
            this.formMode = 'create';
            this.editingGroup = {};
            this.$nextTick(() => this.$bvModal.show(this.formModalId));
        },
        openEdit(item) {
            this.formMode = 'edit';
            this.editingGroup = { ...item };
            this.$nextTick(() => this.$bvModal.show(this.formModalId));
        },
        onSaved() {
            // 생성/수정 후 목록을 다시 불러와 정렬(group_code 오름차순)을 서버 기준으로 유지한다.
            this.fetchGroups();
        },
        async confirmDelete(item) {
            const ok = await this.$showModal({
                type: 'confirm',
                state: 'error',
                title: '평가 그룹 삭제',
                message: `'${item.group_name}' (${item.group_code}) 그룹을 삭제하시겠습니까? 삭제하면 되돌릴 수 없습니다.`,
                modalOptions: { okTitle: '삭제' },
            });
            if (!ok) return;

            this.deletingId = item.id;
            try {
                await this.$axios.delete(`/project_code/groups/${item.id}`);
                this.$EventBus.$emit('toast-success', '삭제 완료', `'${item.group_name}' 그룹을 삭제했습니다.`);
                await this.fetchGroups();
            } catch (e) {
                this.handleDeleteError(e);
            } finally {
                this.deletingId = null;
            }
        },
        handleDeleteError(e) {
            const status = e && e.response && e.response.status;
            if (status === 401) return;
            const serverMsg = this.extractError(e);
            if (status === 409) {
                // 참조중: 삭제 대신 비활성화를 안내한다.
                this.$showModal({
                    type: 'alert',
                    state: 'warning',
                    title: '삭제할 수 없음',
                    message: serverMsg
                        || '이 그룹을 쓰는 프로젝트가 있어 삭제할 수 없습니다. 대신 수정 화면에서 비활성(활성 여부 off)으로 숨겨 주세요.',
                });
                return;
            }
            let msg;
            if (status === 403) {
                msg = serverMsg || '권한이 없습니다. 관리자 계정으로 로그인했는지 확인해 주세요.';
            } else if (status === 404) {
                msg = serverMsg || '이미 삭제된 그룹입니다. 목록을 새로고침합니다.';
                this.fetchGroups();
            } else {
                msg = serverMsg || '삭제 중 오류가 발생했습니다.';
            }
            // 예전 noAutoHide 토스트처럼 오래 남긴다(공용 토스트는 자동 닫힘만 지원)
            this.$EventBus.$emit('toast-failure', '오류', msg, false, 30000);
        },
        extractError(e) {
            const data = e && e.response && e.response.data;
            return getErrorMessage(e, '') || (data && (data.msg || data.detail)) || '';
        },
    },
};
</script>

<style lang="scss" scoped>
.project-group-management {
    code {
        color: #d63384;
        word-break: break-all;
    }
}
</style>
